import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Trash2, Lock, ArrowLeft, Zap } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { eur, lineUnitPrice } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, buyNow, updateBuyNowQty, clearBuyNow } = useCart();
  const isExpress = !!buyNow;
  const checkoutItems = isExpress ? [buyNow] : items;
  const computedSubtotal = isExpress ? lineUnitPrice(buyNow) * buyNow.quantity : subtotal;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    email: "", name: "", phone: "",
    line1: "", line2: "", city: "", region: "", postal_code: "", country: "",
  });
  const [personalisation, setPersonalisation] = useState("");
  const hasBespoke = checkoutItems.some((it) => it.product.category === "jewellery");

  // Clear the transient express buffer when leaving the checkout page
  useEffect(() => () => clearBuyNow(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const checkout = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        items: checkoutItems.map((it) => ({
          product_id: it.product.id,
          variant_index: it.variant_index,
          quantity: it.quantity,
        })),
        customer: {
          email: form.email,
          name: form.name,
          phone: form.phone,
          address: {
            line1: form.line1, line2: form.line2, city: form.city,
            region: form.region, postal_code: form.postal_code, country: form.country,
          },
        },
        origin_url: window.location.origin,
        personalisation,
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  if (checkoutItems.length === 0) {
    return (
      <div className="pt-40 pb-40 text-center px-6">
        <Seo title="Cart" />
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">Your cart is empty</h1>
        <Link to="/shop" className="inline-block mt-10 bg-[#D4AF37] text-[#0C0A09] px-10 py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors">
          Return to Shop
        </Link>
      </div>
    );
  }

  const inputCls = "w-full bg-transparent border-b ee-hairline focus:border-[#D4AF37] outline-none py-3 text-[#FAFAF9] transition-colors placeholder:text-[#57534E]";

  return (
    <div className="pt-32 pb-28 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
      <Seo title="Checkout" />
      <Link to="/shop" className="inline-flex items-center gap-2 text-[#A8A29E] hover:text-[#D4AF37] text-sm mb-8">
        <ArrowLeft size={16} /> Continue shopping
      </Link>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">Checkout</h1>
      {isExpress && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-12 border ee-hairline bg-[#1C1917] px-5 py-4" data-testid="express-banner">
          <span className="inline-flex items-center gap-2 ee-gold-text text-sm uppercase tracking-[0.15em]">
            <Zap size={15} /> Express checkout · single item
          </span>
          <button type="button" onClick={clearBuyNow} data-testid="checkout-full-cart-btn" className="text-[#A8A29E] text-sm hover:text-[#D4AF37] sm:ml-auto underline-offset-4 hover:underline">
            Checkout my full cart instead
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Customer form */}
        <form onSubmit={checkout} className="lg:col-span-7 space-y-10" data-testid="checkout-form">
          <div>
            <p className="ee-overline mb-6">Contact</p>
            <div className="grid sm:grid-cols-2 gap-6">
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="checkout-email" />
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="checkout-name" />
              <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={`${inputCls} sm:col-span-2`} data-testid="checkout-phone" />
            </div>
          </div>
          <div>
            <p className="ee-overline mb-6">Shipping address</p>
            <div className="grid sm:grid-cols-2 gap-6">
              <input required placeholder="Address line 1" value={form.line1} onChange={set("line1")} className={`${inputCls} sm:col-span-2`} data-testid="checkout-line1" />
              <input placeholder="Address line 2 (optional)" value={form.line2} onChange={set("line2")} className={`${inputCls} sm:col-span-2`} />
              <input required placeholder="City" value={form.city} onChange={set("city")} className={inputCls} data-testid="checkout-city" />
              <input placeholder="County / Region" value={form.region} onChange={set("region")} className={inputCls} />
              <input required placeholder="Postal code" value={form.postal_code} onChange={set("postal_code")} className={inputCls} data-testid="checkout-postal" />
              <input required placeholder="Country" value={form.country} onChange={set("country")} className={inputCls} data-testid="checkout-country" />
            </div>
          </div>

          {hasBespoke && (
            <div className="border ee-hairline bg-[#1C1917] p-6" data-testid="bespoke-checkout-note">
              <p className="ee-overline mb-2 ee-gold-text">Bespoke · Made to your measurements</p>
              <p className="text-[#A8A29E] text-sm leading-relaxed mb-5">
                Each bangle is handcrafted to order. After your purchase, our expert craftspeople will personally
                contact you to take your exact wrist measurements and discuss any unique details you'd like
                incorporated into your piece.
              </p>
              <label className="text-xs text-[#57534E]">Personalisation &amp; special requests (optional)</label>
              <textarea
                rows={3}
                value={personalisation}
                onChange={(e) => setPersonalisation(e.target.value)}
                data-testid="checkout-personalisation"
                placeholder="e.g. wrist size if known, engraving, a horse's name, or any design ideas…"
                className={`${inputCls} resize-none mt-1`}
              />
            </div>
          )}

          {error && <p className="text-red-400 text-sm" data-testid="checkout-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            data-testid="pay-now-btn"
            className="w-full bg-[#D4AF37] text-[#0C0A09] py-5 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Lock size={16} /> {loading ? "Redirecting to secure payment…" : `Pay ${eur(computedSubtotal)} securely`}
          </button>
          <p className="text-xs text-[#57534E] text-center">You'll be redirected to Stripe to complete payment securely.</p>
        </form>

        {/* Order summary */}
        <div className="lg:col-span-5">
          <div className="border ee-hairline bg-[#1C1917] p-8 lg:sticky lg:top-28">
            <p className="ee-overline mb-6">Order summary</p>
            <div className="space-y-5">
              {checkoutItems.map((it, i) => (
                <div key={i} className="flex gap-4" data-testid={`summary-item-${i}`}>
                  <div className="h-20 w-16 bg-[#0C0A09] overflow-hidden flex-shrink-0">
                    <img src={it.product.images?.[0]} alt={it.product.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg leading-tight">{it.product.name}</p>
                    {it.variant_index != null && it.product.variants?.[it.variant_index] && (
                      <p className="text-xs text-[#A8A29E]">{it.product.variants[it.variant_index].name}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <button type="button" onClick={() => (isExpress ? updateBuyNowQty(it.quantity - 1) : updateQty(i, it.quantity - 1))} className="border ee-hairline p-1 hover:border-[#D4AF37]"><Minus size={11} /></button>
                      <span className="text-sm">{it.quantity}</span>
                      <button type="button" onClick={() => (isExpress ? updateBuyNowQty(it.quantity + 1) : updateQty(i, it.quantity + 1))} className="border ee-hairline p-1 hover:border-[#D4AF37]"><Plus size={11} /></button>
                      <button type="button" onClick={() => (isExpress ? clearBuyNow() : removeItem(i))} className="ml-auto text-[#A8A29E] hover:text-[#D4AF37]"><Trash2 size={15} strokeWidth={1} /></button>
                    </div>
                  </div>
                  <p className="ee-gold-text text-sm whitespace-nowrap">{eur(lineUnitPrice(it) * it.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="border-t ee-hairline mt-6 pt-6 flex justify-between items-end">
              <span className="text-[#A8A29E]">Subtotal</span>
              <span className="font-display text-3xl ee-gold-text" data-testid="checkout-subtotal">{eur(computedSubtotal)}</span>
            </div>
            <p className="text-xs text-[#57534E] mt-2">Excl. VAT · Shipping arranged after purchase</p>
          </div>
        </div>
      </div>
    </div>
  );
}
